/**
 * What the public rules page and the /game Discord post show of a Hide and
 * Seek round: a switch for each part (on = shown, off = "Revealed at round
 * start"), and a note for players. Everything is shown unless switched off.
 */

import { Accordion, AccordionItem, Field, Switch, Textarea } from '@swtorbit/ui/react';
import {
  HIDE_SEEK_PUBLIC_DISPLAY_LABELS,
  HIDE_SEEK_PUBLIC_NOTES_MAX,
  hideSeekPublicDisplayKeys,
  resolveHideSeekPublicDisplay,
  type HideSeekPublicDisplay,
  type HideSeekPublicDisplayKey,
  type HideSeekRoundSettings,
} from '@swtorbit/shared-socket-events';

interface RulesDisplayEditorProps {
  gameType: HideSeekRoundSettings['gameType'];
  display: Partial<HideSeekPublicDisplay>;
  /** The note for players; absent for the round defaults, which have none. */
  notes?: string;
  onDisplayChange: (display: Partial<HideSeekPublicDisplay>) => void;
  onNotesChange?: (notes: string) => void;
}

/** Only what is switched off is kept: everything else is shown by default. */
function withPart(display: Partial<HideSeekPublicDisplay>, key: HideSeekPublicDisplayKey, shown: boolean): Partial<HideSeekPublicDisplay> {
  const next = { ...display };
  if (shown) delete next[key];
  else next[key] = false;
  // Showing the area again shows the planet too; it can't be shown without it.
  if (key === 'area' && shown) delete next.planet;
  return next;
}

export function RulesDisplayEditor({ gameType, display, notes, onDisplayChange, onNotesChange }: RulesDisplayEditorProps) {
  const resolved = resolveHideSeekPublicDisplay(display);
  const keys = hideSeekPublicDisplayKeys(gameType);
  const secret = keys.filter((key) => !resolved[key]).length;

  return (
    <Accordion boxed>
      <AccordionItem title="Rules page display" meta={secret === 0 ? 'Everything shown' : `${secret} kept secret`}>
        <p className="fs-sm text-slate mt-0">
          What the public rules page and the /game post in Discord show of this round. A part switched off says
          &quot;Revealed at round start&quot; until the round starts.
        </p>
        <div className="d-flex flex-column gap-xs">
          {keys.map((key) => (
            <Switch
              key={key}
              label={HIDE_SEEK_PUBLIC_DISPLAY_LABELS[key]}
              hint={key === 'planet' && !resolved.planet ? 'The area is kept secret with it.' : undefined}
              checked={resolved[key]}
              onChange={(e) => onDisplayChange(withPart(display, key, e.target.checked))}
            />
          ))}
        </div>
        {onNotesChange && (
          <Field label="Note for players" hint={`Shown with the round. Up to ${HIDE_SEEK_PUBLIC_NOTES_MAX} characters.`} className="mt-sm">
            <Textarea
              rows={2}
              maxLength={HIDE_SEEK_PUBLIC_NOTES_MAX}
              value={notes ?? ''}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="e.g. Bring a speeder"
            />
          </Field>
        )}
      </AccordionItem>
    </Accordion>
  );
}
